import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock3, MapPin, Package, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ProductCard from "@/components/product/ProductCard";
import { colorRules } from "@/data/products";

const trustBadges = [
  { icon: Clock3, text: "28+ Years Experience" },
  { icon: MapPin, text: "Pan India Supply" },
  { icon: ShieldCheck, text: "Quality Assured" },
  { icon: Package, text: "Bulk Wholesale" },
];

export default function HomePage({ company, products }) {
  const featuredProducts = products.slice(0, 6);

  return (
    <div className="space-y-16 md:space-y-24">
      <section className="hero-pattern relative overflow-hidden bg-slate-900 text-white">
        <div className="mx-auto grid w-full max-w-7xl items-center gap-10 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-24">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
            <Badge data-testid="hero-badge" className="mb-5 rounded-sm bg-orange-500 text-white">
              Networking Accessories & Telecom Products
            </Badge>
            <h1 data-testid="hero-title" className="font-heading text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
              Complete B2B Networking Catalog for Fast Business Procurement
            </h1>
            <p data-testid="hero-description" className="mt-5 max-w-xl text-sm text-slate-200 sm:text-base">
              Explore networking accessories, routers, fiber optic supplies, racks, and testers. Generate product-specific inquiries instantly.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild data-testid="hero-products-button" className="rounded-sm bg-orange-500 text-white hover:bg-orange-600">
                <Link to="/products" className="inline-flex items-center gap-2">
                  Explore Products <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild data-testid="hero-contact-button" variant="outline" className="rounded-sm border-white bg-transparent text-white hover:bg-white hover:text-slate-900">
                <Link to="/contact">Contact Team</Link>
              </Button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }}>
            <div className="aspect-[4/3] overflow-hidden rounded-sm border border-slate-700">
              <img
                src="https://images.unsplash.com/photo-1744868562210-fffb7fa882d9?auto=format&fit=crop&w=1400&q=80"
                alt="Networking rack setup"
                data-testid="hero-image"
                className="h-full w-full object-cover object-center"
              />
            </div>
            <p data-testid="hero-company-info" className="mt-4 text-sm text-slate-300">
              {company.name} • {company.phone} • GST: {company.gst}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {trustBadges.map((badge) => {
            const Icon = badge.icon;
            return (
              <div
                key={badge.text}
                data-testid={`trust-badge-${badge.text.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-center gap-3 rounded-sm border border-slate-200 bg-white p-4"
              >
                <Icon className="h-5 w-5 text-orange-500" />
                <p className="text-sm font-semibold text-slate-800">{badge.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <h2 data-testid="featured-products-title" className="font-heading text-3xl font-bold text-slate-900 sm:text-4xl">
              Featured Products
            </h2>
            <p data-testid="featured-products-subtitle" className="mt-2 text-sm text-slate-600 sm:text-base">
              Similar catalog style, rebuilt with modern UI and smooth inquiry flow.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {featuredProducts.map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} />
          ))}
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-sm border border-slate-200 bg-white p-8">
          <h2 data-testid="color-rules-title" className="font-heading text-3xl font-bold text-slate-900">
            5 Color Rules Used for Better Conversion
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {colorRules.map((rule) => (
              <div
                key={rule.title}
                data-testid={`color-rule-${rule.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="rounded-sm border border-slate-200 bg-slate-50 p-4"
              >
                <h3 className="font-heading text-lg font-semibold text-slate-900">{rule.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{rule.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
